/**
 * Action-card resource resolution for the in-game Actions panel.
 *
 * Extracted from `CharDetailsPanel` so it can be tested without mounting the
 * panel and its zustand stores, matching the precedent `mergeSpellbookLevels`
 * set for the Spellbook pane. Behaviour is unchanged by the move.
 *
 * Mirrors the Rust `CharacterData::max_resource_uses()` / `resource_remaining()`
 * in `backend/shared/src/types/character/data.rs`. A new server-side resource
 * type needs a branch here too.
 */

/** The character state `resolveCardUses` reads, gathered by the caller. */
export interface CardUsesContext {
  /** `class_abilities` off the permanent blob: the MAX side of each pool. */
  classAbilities: Record<string, unknown>;
  /** `class_resources` off the session blob: the SPENT or REMAINING side. */
  classResources: Record<string, unknown>;
  /** `feature_uses`, keyed by ability display name. */
  featureUses: Record<string, number>;
  /** Character level, the fallback max for sorcery points. */
  level?: number;
  /** `permanent.computed`, for ability mods (Bardic Inspiration reads CHA). */
  computed?: Record<string, unknown>;
}

/**
 * Effectively infinite, for `MaxUses::Unlimited` (level-20 Rage and Wild
 * Shape). Exported so display code can spot it instead of printing the number.
 */
export const UNLIMITED_USES = 1 << 30;

const resolveMax = (raw: unknown): number => {
  if (typeof raw === 'number') return raw;
  // `MaxUses` serializes as a bare int or the string "unlimited" (see the
  // custom impls on the Rust enum). `null` is the OLD untagged encoding, which
  // could not round-trip; accept it so a character saved before that fix still
  // reads as unlimited rather than exhausted.
  if (raw === null) return UNLIMITED_USES;
  if (typeof raw === 'string') {
    return raw.toLowerCase() === 'unlimited' ? UNLIMITED_USES : 0;
  }
  if (raw && typeof raw === 'object' && 'value' in raw) {
    const v = (raw as { value: unknown }).value;
    return typeof v === 'number' ? v : 0;
  }
  return 0;
};
const abilityNum = (obj: Record<string, unknown> | undefined, key: string): number => {
  if (!obj) return 0;
  return resolveMax(obj[key]);
};
const sessionNum = (obj: Record<string, unknown> | undefined, key: string): number => {
  if (!obj) return 0;
  const v = obj[key];
  return typeof v === 'number' ? v : 0;
};

// Compact resolver for ActionCard availability. Cards carry only the
// resource name + amount; we still need the remaining/max from the
// character's class_abilities + class_resources session state. Returns
// null if the resource isn't tracked yet (card surfaces as 1/1).
export const resolveCardUses = (
  ctx: CardUsesContext,
  resource: string,
  cardLabel: string,
  // The card's `resource_requirement.amount`. For `feature_use` this is the
  // CAP, not a per-click cost, so it is the only source of the real max.
  requirementAmount?: number
): { max: number; remaining: number } | null => {
  switch (resource) {
    case 'rage':
      return {
        max: abilityNum(ctx.classAbilities, 'max_rage_uses'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_rage_uses') -
          sessionNum(ctx.classResources, 'rage_uses'),
      };
    case 'bardic_inspiration': {
      const chaMod = (ctx.computed?.ability_mods as Record<string, number> | undefined)?.cha ?? 0;
      const max = Math.max(chaMod, 1);
      return {
        max,
        remaining: max - sessionNum(ctx.classResources, 'bardic_inspiration_uses'),
      };
    }
    case 'channel_divinity': {
      const max =
        abilityNum(ctx.classAbilities, 'max_cleric_channel_divinity_uses') ||
        abilityNum(ctx.classAbilities, 'max_channel_divinity_uses');
      return { max, remaining: max - sessionNum(ctx.classResources, 'channel_divinity_uses') };
    }
    case 'wild_shape':
      return {
        max: abilityNum(ctx.classAbilities, 'max_wild_shape_uses'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_wild_shape_uses') -
          sessionNum(ctx.classResources, 'wild_shape_uses'),
      };
    case 'second_wind':
      return {
        max: abilityNum(ctx.classAbilities, 'max_second_wind_uses'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_second_wind_uses') -
          sessionNum(ctx.classResources, 'second_wind_uses'),
      };
    case 'action_surge':
      return {
        max: abilityNum(ctx.classAbilities, 'max_action_surge_uses'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_action_surge_uses') -
          sessionNum(ctx.classResources, 'action_surge_uses'),
      };
    case 'focus_points':
      return {
        max: abilityNum(ctx.classAbilities, 'max_focus_points'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_focus_points') -
          sessionNum(ctx.classResources, 'focus_points'),
      };
    case 'lay_on_hands_pool':
      return {
        max: abilityNum(ctx.classAbilities, 'max_lay_on_hands_pool'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_lay_on_hands_pool') -
          sessionNum(ctx.classResources, 'lay_on_hands_pool'),
      };
    case 'arcane_recovery': {
      // A boolean on the wire, not a counter: SRD 5.2.1 is "Once you use this
      // feature, you can't do so again until you finish a Long Rest". Absent
      // means available, matching the Rust evaluator
      // (`CharacterData::resource_remaining`, data.rs). Without this case the
      // switch fell through to `null`, and the caller defaults an unresolved
      // card to 1/1 — so a Wizard who already recovered this rest saw a live
      // card and clicked into a backend rejection.
      const available = ctx.classResources.arcane_recovery_available;
      return { max: 1, remaining: available === false ? 0 : 1 };
    }
    case 'sorcery_points': {
      const max = abilityNum(ctx.classAbilities, 'max_sorcery_points') || (ctx.level ?? 0);
      // `sorcery_points`, NOT `sorcery_points_remaining`: Rust renames the
      // field on the wire (`#[serde(rename = "sorcery_points")]` in
      // session_types.rs) and the TS `ClassResources` agrees. Reading the
      // Rust-side name gave `undefined` forever, so `remaining` always fell
      // through to `max` and every metamagic card read a full pool and
      // stayed clickable with 0 SP left. `tsc` cannot catch it because the
      // blob is cast to `Record<string, unknown>` above.
      const sr = ctx.classResources.sorcery_points;
      const remaining = typeof sr === 'number' ? sr : max;
      return { max, remaining };
    }
    case 'feature_use': {
      // Keyed on ability label, matching the enricher's feature_uses
      // map keys.
      const used = ctx.featureUses[cardLabel] ?? 0;
      // The cap is the card's own `amount`. On a FeatureUse cost that value
      // is the MAX USES per long rest, not a per-click cost: the backend
      // gates on `feature_uses[label] >= amount` and increments by 1
      // (availability.rs / use_ability). Hardcoding 1 here made every
      // multi-use feature dead after a single click, and it read `1/1`
      // before that. Sorcerer Innate Sorcery ships `amount: 2`, and Dwarf
      // Stonecunning / Orc Adrenaline Rush / Goliath Giant Ancestry all use
      // `feature_use_cost(proficiency_bonus)`, so 2 at low level.
      const max = requirementAmount && requirementAmount > 0 ? requirementAmount : 1;
      return { max, remaining: Math.max(0, max - used) };
    }
    // Ranger, both real resource-costed cards the backend evaluates. Untracked
    // here, they fell through to `null` and stayed offered in the slash menu
    // after exhaustion — the one thing the gate exists to stop.
    case 'natures_veil':
      return {
        max: abilityNum(ctx.classAbilities, 'max_natures_veil_uses'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_natures_veil_uses') -
          sessionNum(ctx.classResources, 'natures_veil_uses'),
      };
    case 'tireless':
      return {
        max: abilityNum(ctx.classAbilities, 'max_tireless_uses'),
        remaining:
          abilityNum(ctx.classAbilities, 'max_tireless_uses') -
          sessionNum(ctx.classResources, 'tireless_uses'),
      };
    default:
      return null; // unknown resource: card still renders as always-on
  }
};
